import type { ColumnDef } from "@tanstack/react-table";

import { DataTableColumnHeader } from "@/components/DataTable/Columns/DataTableColumnHeader";
import type { RequiredOnly } from "@dresscode-frontend/utility-types";

import Icon from "@/components/Icon/Icon";
import type { IconName } from "@/components/Icon/iconMap";
import { HStack } from "@/components/Layout";
import { Text } from "@/components/Typography/Text";
import { cn } from "@/lib/cn";
import type { HelperOptions } from "../types";

type NestedKeys<T> = T extends object
  ? {
      [K in keyof T]: K extends string
        ? `${K}` | `${K}.${NestedKeys<T[K]>}`
        : never;
    }[keyof T]
  : never;

type Options = RequiredOnly<HelperOptions, "i18nKey"> & {
  /**
   * 機能強化によりLeading / TraidlingIconを差し込めるようになった。
   * 後方互換性を保つため、デフォルトで表示するようにしている。
   */
  showLeadingIcon?: boolean;
  leadingIcon?: IconName;
  trailingIcon?: IconName;
};

/** リンクを表示するカラム */
export function simpleLinkColumn<TData>(
  id: NestedKeys<TData> & string,
  options: Options,
): ColumnDef<TData> {
  const {
    i18nKey,
    minWidth = "160px",
    maxWidth,
    sortable = false,
    showLeadingIcon = true,
    leadingIcon = "LinkIcon",
    trailingIcon,
  } = options;
  return {
    accessorKey: id,
    meta: { i18nKey, thStyle: { minWidth, ...(maxWidth && { maxWidth }) } },
    enableSorting: sortable,
    header: DataTableColumnHeader,
    cell: ({ getValue }) => {
      const value = getValue();
      if (!value) return null;
      return (
        <a href={value} target="_blank" rel="noopener noreferrer">
          <HStack className="gap-2 items-center">
            {showLeadingIcon && <Icon iconName={leadingIcon} />}
            <Text
              variant="label"
              className={cn(
                "whitespace-nowrap overflow-hidden text-ellipsis",
                maxWidth && `max-w-[${maxWidth}]`,
              )}
            >
              {value}
            </Text>
            {trailingIcon && <Icon iconName={trailingIcon} />}
          </HStack>
        </a>
      );
    },
  } satisfies ColumnDef<TData, string>;
}
